import Icon from '@mdi/react';
import { IconButton, Stack, SxProps } from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { vars } from '../../../Theme';
import AppTooltip from '../AppTooltip/AppTooltip';
import { IDocDefaultFields } from '../../../redux/project/documents/sliceProjectDocuments';
import { GridValidRowModel } from '@mui/x-data-grid-pro';
import { useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../redux/store';
import { getDocTypeOrDefault } from '../../../util/DocUtils';
import { getFileTypeOrDefault } from '../../../util/FileUtils';
import { getDocTableActionsConfig } from '../../../redux/project/sliceProjectActions';
import { type ActionName } from './DocTableActions';

/** Styles for the Stack Component. */
const stackSx: SxProps = {
    display: 'flex',
    flexDirection: 'row',
    alignSelf: 'center',
    flex: 0,
};

/**
 * Buttons that are shown when the user hovers over a cell in the DocTable.
 */
export default ({ document, mainDocument, isEditable }: IDocTableHoverButtonsProps) => {
    /** Configure component translation. */
    const { i18n } = useTranslation();

    /** Get the replicaId from the react router. */
    const { replicaId, areaName } = useParams();

    /** Get relevant values from Redux store. */
    const docTypes = useSelector((store: IAppReduxStore) => store.projectDocTypes.data);
    const actionsConfigData = useSelector(getDocTableActionsConfig);
    const fileTypes = useSelector((store: IAppReduxStore) => store.projectFileTypes.data);
    const fallback = useSelector((store: IAppReduxStore) => store.projectFileTypes.default);

    const docType = getDocTypeOrDefault(document, docTypes);

    const buttons = [
        ...(docTypes.find(x => x.name === docType)?.hoverButtons ?? []),
        ...(areaName === 'import' && isEditable ? ['editDocumentNumber'] : []),
    ];

    /* Gets file type and its properties. **/
    const documentFileType = useMemo(
        () => getFileTypeOrDefault(document?.Files?.FileName),
        [document, fileTypes, fallback],
    );

    /** No buttons are defined or document doesn't exist - stop here. */
    if (!buttons?.length || !actionsConfigData || !document) {
        return <></>;
    }

    /**
     * Creates a button for the component.
     * @param btnId - Button id.
     */
    const createBtn = (btnId: ActionName) => {
        if (
            !actionsConfigData?.[btnId] ||
            (btnId === 'view' && !documentFileType?.extensions?.length) ||
            (btnId === 'changeStatus' &&
                (areaName !== 'mytasks' ||
                    document.ViewIcon_DocumentRelease !== 'View_DocStatusEdit_NotReleased')) ||
            (btnId === 'changeStatusAdmin' && areaName !== 'documents') ||
            (btnId === 'removeAdditionalDocument' &&
                areaName !== 'import' &&
                (areaName !== 'mytasks' ||
                    document.ViewIcon_DocumentRelease !== 'View_DocStatusEdit_NotReleased'))
        ) {
            return null;
        }

        /** Command definition. */
        const { icon, translations, onClick } = actionsConfigData[btnId];

        /**
         * Handles the Button's onclick event.
         */
        const handleOnClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();

            onClick({
                replicaId,
                universalId: document.UniversalID,
                idDocIndex: document.IdDocIndex,
                fileName: document.Files?.FileName,
                documentNumber: document?.DocNumber || document['docNumber'],
                referencedMainDocId: mainDocument,
            });
        };

        return actionsConfigData?.[btnId] ? (
            <AppTooltip key={btnId} title={translations[i18n.language]} placement='top-start'>
                <IconButton
                    //disabled
                    onClick={handleOnClick}
                    sx={{
                        borderRadius: 1,
                        p: '0px 8px',
                    }}
                    color='primary'
                >
                    <Icon path={icon} size={1} color={vars.font} />
                </IconButton>
            </AppTooltip>
        ) : null;
    };

    return <Stack sx={stackSx}>{buttons.map(createBtn).filter(Boolean)}</Stack>;
};

/**
 * Interface that describes the props for a DocTableHoverButtons components.
 */
export interface IDocTableHoverButtonsProps {
    /** Target document. */
    document: IDocDefaultFields | GridValidRowModel;

    /** Main document. */
    mainDocument?: string;

    /** (OPTIONAL) Defines if field is editable*/
    isEditable?: boolean;
}
